import Carousel, { CarouselProps } from '@/components/ui/custom/Carousel';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { ProjectFilters } from '@/data/services/projectServices';
import { Project } from '@/models/Project';
import ProjectSectionHeader from '@/pages/project/ProjectSectionHeader';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import Card from '../cards/Card';
import ProjectCardsFactory, {
  ProjectCardsMode,
} from '../cards/factory/ProjectCardsFactory';

export type ProjectsSections =
  | 'highlights'
  | 'recents'
  | 'viewed'
  | 'me'
  | 'hackathon'
  | 'ranking';
type ProjectWithRank = Project & { rank?: number };
type ProjectCarouselProps = {
  section: ProjectsSections;
  filters?: ProjectFilters;
  onMutate?: (project: Project) => void;
  onFilter?: (filters: ProjectFilters) => void;
  route?: string;
  projects?: ProjectWithRank[];
  isLoading?: boolean;
  error: unknown;
  hideSeeAll?: boolean;
} & Omit<CarouselProps, 'children'>;

export default function ProjectCarousel({
  section,
  filters,
  route,
  error,
  onMutate,
  onFilter,
  projects = [],
  isLoading,
  hideSeeAll,
  ...carouselProps
}: ProjectCarouselProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'projects.page' });
  const { t: tCarousel } = useTranslation('translation', {
    keyPrefix: 'projects.carousel',
  });

  const navigate = useNavigate();

  const projectsErrorDetail = getErrorMessage(error);

  const divClassName = 'flex w-full h-[256px] justify-center items-center';

  const loadingCards = useMemo(
    () =>
      Array.from({ length: 20 }).map((_, index) => (
        <Card.Skeleton key={index} />
      )),
    [],
  );

  const convertSectionToMode = (
    section: ProjectsSections,
  ): ProjectCardsMode => {
    if (['highlights', 'recents', 'viewed'].includes(section)) return 'general';
    return section as ProjectCardsMode;
  };

  return (
    <div className="flex flex-col bg-accent-content/30 rounded-lg border border-neutral-content py-2.5 pl-2.5 gap-2.5">
      <ProjectSectionHeader
        section={section}
        filters={filters}
        onFilter={onFilter}
        button={
          <ConditionalRenderer condition={!hideSeeAll}>
            <MainButton
              text={tCarousel('seeAll')}
              onClick={() => {
                navigate(route || 'section', {
                  state: {
                    filters,
                    section,
                  },
                });
              }}
              color="custom"
              className={twJoin(
                'font-600 !text-14 text-primary gap-1 flex-row-reverse mr-2.5',
              )}
              icon={
                <ChevronRightIcon
                  className={twJoin(
                    'w-4 h-4 ease-in-out transition-all duration-200',
                  )}
                />
              }
            />
          </ConditionalRenderer>
        }
      />
      <Carousel {...carouselProps}>
        <ConditionalRenderer condition={isLoading}>
          {loadingCards}
        </ConditionalRenderer>

        <ConditionalRenderer condition={projectsErrorDetail}>
          <div className={divClassName}>
            <Text
              format="rubik-500"
              text={projectsErrorDetail}
              className="text-accent"
            />
          </div>
        </ConditionalRenderer>
        <ConditionalRenderer condition={projects.length}>
          {projects.map(project => (
            <ProjectCardsFactory
              key={project.id}
              project={project}
              onMutate={onMutate}
              mode={convertSectionToMode(section)}
              rank={project.rank}
            />
          ))}
        </ConditionalRenderer>
        <ConditionalRenderer
          condition={!isLoading && !projectsErrorDetail && !projects.length}
        >
          <div className="flex w-full h-[256px] justify-center items-center">
            <Text text={t('noProjectsFound')} />
          </div>
        </ConditionalRenderer>
      </Carousel>
    </div>
  );
}
